import React, { useEffect, useState } from "react";
import { BasicInfoProps } from "src/interfaces/functionalComponentInterface";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { TooltipComponent } from "./ui/TooltipComponent";

const BasicInfo: React.FC<BasicInfoProps> = ({
  startDate,
  setStartDate,
  numberOfWeeks,
  setNumberOfWeeks,
  isActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    numberOfWeeks.toString()
  );

  useEffect(() => {
    setInputValue(numberOfWeeks.toString());
  }, [numberOfWeeks]);

  const handleNumberInputChange = (
    value: string,
    setter: (val: number) => void
  ) => {
    const numericValue = value.replace(/^0+(?!$)/, "");
    if (/^\d*$/.test(numericValue)) {
      setter(Number(numericValue));
    }
  };

  const handleFocus = () => {
    if (inputValue === "0") setInputValue("");
  };

  const handleBlur = () => {
    if (inputValue === "") {
      setInputValue("0");
      setNumberOfWeeks(0);
    }
  };

  return (
    <div
      className={`flex flex-col items-center z-10 mx-auto max-w-[600px] w-full card-form ${
        isActive ? "show-form" : "hide-form"
      }`}
    >
      <div className="w-full md:px-0 gap-0">
        <div
          className={`flex flex-row items-center justify-between mt-10
            min-h-[64px] py-[16px] px-[24px] border border-solid rounded-lg 
            ${"bg-[#E7F2FF] border-[#E7F2FF]"}`}
        >
          <div className="flex fle-row items-center gap-[16px] w-full">
            <p className="text-[#1E1E1E] text-[14px] leading-[100%] montserrat font-[700]">
              Set Your Onboarding Timeline
            </p>
          </div>
        </div>
        <div className="p-6 md:p-10 bg-white border border-[#DFDFDF] rounded-lg">
          <div className="mb-8">
            <p className="text-sm md:text-base text-left">
              Choose your start date and specify the number of weeks for your
              client onboarding plan. Your projected compensation depends on
              your onboarding <strong>timeline</strong>, the{" "}
              <strong>
                number of client households transitioned each week
              </strong>
              , and the <strong>total account value</strong> transferred.
            </p>
          </div>

          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:gap-6">
            <div className="w-full md:w-1/2">
              <Label htmlFor="start-day" className="block text-sm md:text-base">
                Start Day
              </Label>
              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full mt-2 text-sm font-medium md:text-base"
                  >
                    {startDate.toLocaleDateString("en-US")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="center" className="p-2 w-auto">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      if (date && date.getDay() !== 0 && date.getDay() !== 6) {
                        setStartDate(date);
                        setIsOpen(false);
                      }
                    }}
                    disabled={(date) =>
                      date.getDay() === 0 || date.getDay() === 6
                    }
                    initialFocus
                    className="text-sm font-medium md:text-base"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="w-full md:w-1/2">
              <div className="flex flex-row items-center gap-2 mb-2">
                <Label
                  htmlFor="number-of-weeks"
                  className="block text-sm font-medium md:text-base"
                >
                  Number of Weeks
                </Label>
                <TooltipComponent
                  id="number-of-weeks-tooltip"
                  content="This refers to how many weeks it will take you to fully transition your client base to Optimize."
                  width={16}
                />
              </div>
              <Input
                type="text"
                id="number-of-weeks"
                value={inputValue}
                onChange={(e) =>
                  handleNumberInputChange(e.target.value, setNumberOfWeeks)
                }
                onBlur={handleBlur}
                onFocus={handleFocus}
                className="mt-2 text-center text-sm font-medium md:text-base"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfo;
